import { useEffect, useState } from "react";

export default function Architecture() {
  return (
    <section id="architecture" className="architecture">
      <div className="container">
        <div className="section-head">
          <div className="label reveal">/ 03 — Architecture</div>
          <div>
            <h2 className="reveal d1">A privacy-first stack, traced from your device to the network and back.</h2>
            <p className="lede reveal d2">
              No persistent store sits between request and response. The architecture eliminates retention as a primitive — not as a feature flag.
            </p>
          </div>
        </div>

        <div className="arch-stage reveal d2">
          <span className="arch-corner tl">Schema · v1.0</span>
          <span className="arch-corner tr">QXAI · INFERENCE PATH</span>
          <span className="arch-corner bl">A → B → A · stateless</span>
          <span className="arch-corner br">● live</span>
          <Diagram />
        </div>

        <div className="arch-legend">
          <div className="item">
            <div className="swatch s1"></div>
            <div>
              <h4>Boundary</h4>
              <p>The edge of your device. Cryptographic root of trust lives inside.</p>
            </div>
          </div>
          <div className="item">
            <div className="swatch s2"></div>
            <div>
              <h4>Ephemeral compute</h4>
              <p>Nodes that compute and forget. No disk, no log, no record.</p>
            </div>
          </div>
          <div className="item">
            <div className="swatch s3"></div>
            <div>
              <h4>Encrypted packet</h4>
              <p>Data in motion is opaque to us. Hash is your key.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Diagram() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf;
    const loop = () => { setTick((t) => (t + 1) % 100000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const t = tick / 60;
  const packets = [0, 0.33, 0.66].map((o) => ((t * 0.18) + o) % 1);
  const packetX = (p) => 100 + p * 800;

  return (
    <svg viewBox="0 0 1000 560" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="grid2" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--rule-soft)" strokeWidth="0.5" />
        </pattern>
        <pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--rule)" strokeWidth="1" />
        </pattern>
      </defs>

      <rect width="1000" height="560" fill="url(#grid2)" opacity="0.5" />

      {/* User Device */}
      <g>
        <rect x="40" y="120" width="240" height="320" fill="var(--bg)" stroke="var(--fg)" strokeWidth="1" />
        <text x="50" y="100" fontFamily="var(--mono)" fontSize="11" fill="var(--fg-faint)" letterSpacing="2">USER DEVICE · CLIENT</text>
        <rect x="64" y="200" width="192" height="120" fill="url(#hatch)" opacity="0.7" />
        <rect x="64" y="200" width="192" height="120" fill="none" stroke="var(--accent)" strokeWidth="1" />
        <text x="160" y="180" fontFamily="var(--mono)" fontSize="10" fill="var(--accent)" textAnchor="middle" letterSpacing="2">ENCRYPTED · LOCAL</text>
        <text x="160" y="265" fontFamily="var(--serif)" fontSize="22" fill="var(--fg)" textAnchor="middle">your data</text>
        <text x="160" y="288" fontFamily="var(--mono)" fontSize="9" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">HASH HELD HERE ONLY</text>
        <g transform="translate(64, 350)">
          <text fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" letterSpacing="1.5">ACCT_ID:</text>
          <text x="70" fontFamily="var(--mono)" fontSize="10" fill="var(--fg)">— anonymous</text>
          <text y="18" fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" letterSpacing="1.5">PAYMENT:</text>
          <text x="70" y="18" fontFamily="var(--mono)" fontSize="10" fill="var(--fg)">— unlinked</text>
          <text y="36" fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" letterSpacing="1.5">EMAIL:</text>
          <text x="70" y="36" fontFamily="var(--mono)" fontSize="10" fill="var(--fg)">— hashed only</text>
        </g>
        <circle cx="280" cy="280" r="6" fill="var(--accent)" />
        <circle cx="280" cy="280" r="6" fill="none" stroke="var(--accent)" opacity="0.4">
          <animate attributeName="r" from="6" to="14" dur="1.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.5" to="0" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Network */}
      <g>
        <line x1="280" y1="280" x2="720" y2="280" stroke="var(--rule)" strokeWidth="1" strokeDasharray="4 4" />
        {packets.map((p, i) => (
          <g key={i}>
            <rect x={packetX(p) - 8} y={272} width="16" height="16" fill="var(--accent)"
              opacity={p < 0.05 || p > 0.95 ? 0 : 0.9} />
            <text x={packetX(p)} y={296} fontFamily="var(--mono)" fontSize="8" fill="var(--bg)" textAnchor="middle"
              opacity={p < 0.1 || p > 0.9 ? 0 : 1}>E2E</text>
          </g>
        ))}
        <g transform="translate(440, 200)">
          <rect width="120" height="160" fill="none" stroke="var(--rule)" strokeWidth="0.5" strokeDasharray="2 4" />
          <text x="60" y="-8" fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">COMPUTE</text>
          <text x="60" y="180" fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">EPHEMERAL</text>
          <g transform="translate(60, 80)">
            <circle r="22" fill="none" stroke="var(--warn)" strokeWidth="1" />
            <line x1="-16" y1="16" x2="16" y2="-16" stroke="var(--warn)" strokeWidth="1" />
            <text y="40" fontFamily="var(--mono)" fontSize="8" fill="var(--warn)" textAnchor="middle" letterSpacing="2">NO LOG</text>
          </g>
        </g>
      </g>

      {/* Inference Network */}
      <g>
        <rect x="720" y="120" width="240" height="320" fill="var(--bg)" stroke="var(--fg)" strokeWidth="1" />
        <text x="730" y="100" fontFamily="var(--mono)" fontSize="11" fill="var(--fg-faint)" letterSpacing="2">QXAI INFERENCE NETWORK</text>
        <g transform="translate(740, 150)">
          {Array.from({ length: 20 }).map((_, i) => {
            const col = i % 4, row = Math.floor(i / 4);
            const active = ((Math.floor(t * 2) + i) % 5) === 0;
            return (
              <rect key={i} x={col * 50} y={row * 50} width="36" height="36"
                fill={active ? "var(--accent)" : "var(--bg-1)"} stroke="var(--rule)" strokeWidth="0.5"
                opacity={active ? 0.9 : 1} />
            );
          })}
        </g>
        <text x="840" y="420" fontFamily="var(--mono)" fontSize="9" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">STATELESS · ROTATING</text>
        <line x1="720" y1="280" x2="730" y2="280" stroke="var(--accent)" strokeWidth="1" />
      </g>

      <g fontFamily="var(--mono)" fontSize="10" fill="var(--fg-faint)" letterSpacing="1.5">
        <text x="160" y="490" textAnchor="middle">[01] data encrypted before leaving device</text>
        <text x="500" y="490" textAnchor="middle">[02] inference runs on rotating, stateless nodes</text>
        <text x="840" y="490" textAnchor="middle">[03] result returns; nothing is retained</text>
      </g>
      <g stroke="var(--rule-soft)" strokeWidth="0.5">
        <line x1="60" y1="500" x2="260" y2="500" />
        <line x1="400" y1="500" x2="600" y2="500" />
        <line x1="740" y1="500" x2="940" y2="500" />
      </g>
    </svg>
  );
}
